import SignInIcon from "@crescentParts/Icons/SignInIcon";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import User from "@insite/mobius/Icons/User";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Menu, { MenuPresentationProps } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
}

const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
    hasChangeLastQty:
        state.pages.vmiBins.selectVmiBinsChangedQty &&
        Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
    orderConfirmationPageUrl: getPageLinkByPageType(state, "OrderConfirmationPage")?.url,
    onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    menu?: MenuPresentationProps;
    myAccountLink?: LinkPresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 30px;
            outline: none;
            flex-direction: column;
        `,
    },
    titleIcon: {
        size: 20,
    },
    titleTypography: {
        color: "text.main",
        css: css`
            text-transform: capitalize;
            font-size: 14px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            max-width: 70px;
                        `,
                        css`
                            max-width: 150px;
                        `,
                    ],
                    "min",
                )}
        `,
        ellipsis: true,
        transform: "capitalize",
    },
    signOutClickable: {
        css: css`
            /* flex: 0 0 auto;
            margin-left: 30px;
            text-transform: uppercase; */
            width: 100%;
            min-height: 42px;
            &:hover {
                background: ${getColor("common.background")};
            }
            padding: 10px 15px 10px 20px;
            color: ${getColor("text.accent")};
            display: block;
            box-sizing: border-box;
            font-size: 16px;
        `,
    },
    menu: {
        cssOverrides: {
            wrapper: css`
                flex: 0 1 auto;
                min-width: 0;
                height: 100%;
                position: relative;
                display: flex;
                align-items: center;
                cursor: pointer;
                > ul[class^="MenuStyle"] {
                    top: 100%;
                    right: -12px;
                    > li {
                        > a {
                            > [class^="TypographyStyle"] {
                                color: ${getColor("text.accent")};
                                font-size: 16px;
                            }
                        }
                    }
                }
            `,
            menu: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                position: fixed;
                                right: 10px;
                            `,
                            css`
                                position: fixed;
                                right: 10px;
                            `,
                            css`
                                position: fixed;
                                right: 10px;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
        },
    },
    myAccountLink: {
        css: css`
            > span {
                display: flex;
                align-items: center;
                flex-direction: column;
            }
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

let icon: React.ComponentType<IconPresentationProps>;
export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    orderConfirmationPageUrl,
    onePageCheckoutConfirmationPageUrl,
}: Props) => {
    const showIcon = fields.visibilityState === "both" || fields.visibilityState === "icon";
    const showLabel = fields.visibilityState === "both" || fields.visibilityState === "label";

    if (fields.icon === "User") {
        icon = SignInIcon;
    }

    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = myAccountPageLink?.children;
    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
                return;
            }
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (
                returnUrl === "/" ||
                (currentUserIsGuest && orderConfirmationPageUrl && returnUrl.indexOf(orderConfirmationPageUrl) > -1) ||
                (currentUserIsGuest &&
                    onePageCheckoutConfirmationPageUrl &&
                    returnUrl.indexOf(onePageCheckoutConfirmationPageUrl) > -1)
            ) {
                history.push(`${signInUrl}`);
                return;
            }

            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
            e.preventDefault();
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {fields.includeAccountMenu && userName && !currentUserIsGuest && (
                <Menu
                    descriptionId="accountMenu"
                    menuItems={menuItems ?? []}
                    displayChangeCustomerLink={displayChangeCustomerLink}
                    maxDepth={1}
                    menuTrigger={
                        <Clickable {...styles.titleClickable} data-test-selector="header_signIn">
                            <Link href="/MyAccount" {...styles.myAccountLink}>
                                {showIcon && <Icon {...styles.titleIcon} src={SignInIcon} title={userName} />}
                                {showLabel && (
                                    <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                        {userName}
                                    </Typography>
                                )}
                            </Link>
                        </Clickable>
                    }
                    {...styles.menu}
                    data-test-selector="header_accountMenu"
                >
                    {userName && !currentUserIsGuest && (
                        <Clickable
                            {...styles.signOutClickable}
                            onClick={onSignOutHandler}
                            data-test-selector="headerSignOutLink"
                        >
                            {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                        </Clickable>
                    )}
                </Menu>
            )}

            {(!fields.includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                <Clickable
                    {...styles.titleClickable}
                    onClick={onSignInHandler}
                    href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                    data-test-selector="header_signIn"
                >
                    {showIcon && <Icon {...styles.titleIcon} src={SignInIcon} title={signInStatusText} />}
                    {showLabel && <Typography {...styles.titleTypography}>{signInStatusText}</Typography>}
                </Clickable>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderSignIn)),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
